/**
 * Pure model builder for the student "My Certifications" view.
 * No React, no I/O: takes already-fetched API data and returns what the view renders.
 */

export const EXPIRING_DAYS = 60;
export const STATUSES = ["active", "expiring", "expired", "missing"];
const DAY_MS = 86400000;
const MAX_AXIS_YEARS = 8;

/** Parses "YYYY-MM-DD" (or ISO) as a local date at midnight; returns null when absent or invalid. */
export function parseDate(value) {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  const date = match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Whole days from `now` to `expires` (negative when past); null for no expiry. */
export function daysUntil(expires, now) {
  const end = parseDate(expires);
  if (!end) return null;
  return Math.round((end - startOfDay(now)) / DAY_MS);
}

/** active | expiring | expired. A record without an expiry date is a lifetime credential (active). */
export function statusForCert(cert, now, expiringDays = EXPIRING_DAYS) {
  const days = daysUntil(cert?.expires, now);
  if (days === null) return "active";
  if (days < 0) return "expired";
  return days <= expiringDays ? "expiring" : "active";
}

function findCertForType(certs, certType) {
  return (
    certs.find((c) => c.certTypeId === certType.id) ||
    certs.find((c) => c.name === certType.name) ||
    null
  );
}

/** Only an external http(s) link can be shared; uploaded documents live behind authenticated /api/ URLs. */
export function shareableUrl(cert) {
  const url = cert?.evidenceUrl;
  return typeof url === "string" && /^https?:\/\//i.test(url) ? url : null;
}

function buildAxis(rows, completions, now) {
  const year = now.getFullYear();
  const years = [year - 1, year + 1];
  rows.forEach((row) => {
    if (row.issued) years.push(row.issued.getFullYear());
    if (row.expiresAt) years.push(row.expiresAt.getFullYear());
  });
  completions.forEach((c) => c.issued && years.push(c.issued.getFullYear()));
  let startYear = Math.min(...years);
  const endYear = Math.max(...years);
  startYear = Math.max(startYear, endYear - (MAX_AXIS_YEARS - 1));
  const start = new Date(startYear, 0, 1).getTime();
  const end = new Date(endYear + 1, 0, 1).getTime();
  const pct = (time) => Math.min(100, Math.max(0, ((time - start) / (end - start)) * 100));
  const labels = [];
  for (let y = startYear; y <= endYear; y += 1) labels.push({ year: y, left: pct(new Date(y, 0, 1).getTime()) });
  return { startYear, endYear, labels, todayPct: pct(startOfDay(now).getTime()), pct };
}

function barFor(row, axis) {
  const from = row.issued ? axis.pct(row.issued.getTime()) : null;
  const to = row.expiresAt ? axis.pct(row.expiresAt.getTime()) : 100;
  if (from === null) return null;
  return { left: from, width: Math.max(to - from, 1.5) };
}

/**
 * @param {object} input
 * @param {Array} input.certs user certifications (id,name,certTypeId,issuer,certNumber,issueDate,expires,evidenceUrl,source)
 * @param {Array} input.requiredTypes tenant certification types (id,name,issuer)
 * @param {Array} input.completions class completion certificates (certificateId,courseId,name,certNumber,issueDate)
 * @param {Date} [input.now]
 */
// missingTypeIds (optional Set of type ids): only these types can show as Missing; undefined means every required type.
export function buildCertificationsModel({ certs = [], requiredTypes = [], completions = [], missingTypeIds, now = new Date() }) {
  const tracked = certs.filter((c) => c.source !== "COURSE_COMPLETION");

  const earnedRows = tracked.map((cert) => {
    // A self-added cert outside the tenant's required types still needs a type for the details/renew dialog.
    const certType = requiredTypes.find((t) => t.id === cert.certTypeId)
      || { id: cert.certTypeId ?? null, name: cert.name, issuer: cert.issuer };
    return {
      key: `cert-${cert.id}`,
      kind: "cert",
      name: certType?.name || cert.name || "Certification",
      issuer: cert.issuer || certType?.issuer || "",
      certNumber: cert.certNumber || "",
      issued: parseDate(cert.issueDate),
      expiresAt: parseDate(cert.expires),
      status: statusForCert(cert, now),
      daysLeft: daysUntil(cert.expires, now),
      shareUrl: shareableUrl(cert),
      hasDocument: Boolean(cert.evidenceUrl),
      cert,
      certType,
    };
  });

  const missingRows = requiredTypes
    .filter((type) => !missingTypeIds || missingTypeIds.has(String(type.id)))
    .filter((type) => !findCertForType(tracked, type))
    .map((type) => ({
      key: `missing-${type.id}`,
      kind: "missing",
      name: type.name,
      issuer: type.issuer || "",
      certNumber: "",
      issued: null,
      expiresAt: null,
      status: "missing",
      daysLeft: null,
      shareUrl: null,
      hasDocument: false,
      cert: null,
      certType: type,
    }));

  const completionPins = completions.map((c) => ({
    key: `completion-${c.certificateId}`,
    certificateId: c.certificateId,
    name: c.name || "Class completion certificate",
    certNumber: c.certNumber || "",
    issued: parseDate(c.issueDate),
  })).filter((c) => c.issued);

  const axis = buildAxis(earnedRows, completionPins, now);
  const withBar = (row) => ({ ...row, bar: row.kind === "cert" ? barFor(row, axis) : null });
  const earned = earnedRows.map(withBar);
  const pins = completionPins.map((pin) => ({ ...pin, left: axis.pct(pin.issued.getTime()) }));

  const countOf = (status) => earned.filter((r) => r.status === status).length;
  const counts = {
    active: countOf("active"),
    expiring: countOf("expiring"),
    expired: countOf("expired"),
    missing: missingRows.length,
  };

  return {
    earned,
    missing: missingRows,
    renewals: earned.filter((r) => r.status === "expired"),
    pins,
    counts,
    earnedCount: earned.length,
    renewalsCount: counts.expired,
    hasRequirements: requiredTypes.length > 0,
    axis: { labels: axis.labels, todayPct: axis.todayPct, startYear: axis.startYear, endYear: axis.endYear },
  };
}

/**
 * Rows for one tab, optionally narrowed to a single status (the status tiles).
 * Renewals only ever lists expired certifications; the Missing row belongs to Earned.
 */
export function selectRows(model, tab, statusFilter) {
  const base = tab === "renewals" ? model.renewals : [...model.earned, ...model.missing];
  const rows = statusFilter ? base.filter((r) => r.status === statusFilter) : base;
  const pins = tab === "earned" && !statusFilter ? model.pins : [];
  return { rows, pins };
}
